/**
 * contentValidators/semanticDistractorChecks.ts
 *
 * S2-01 — Semantic distractor coherence validator.
 *
 * Catches a specific failure mode observed during the TAA-ONT batch audits
 * (e.g. TAA-ONT-VOORZETSEL-001/004 pre-fix): a language-module MC question
 * embeds a sentence between quotes and asks about a part of THAT sentence,
 * but the option strings are copy-paste hallucinations from a different
 * question and share no significant tokens with the embedded sentence.
 *
 * Heuristic (intentionally conservative — emits at most ONE finding per
 * question to avoid noise):
 *
 *   1. Only runs for language modules (Taalbegrip, SpellingRegel, SpellingWW).
 *   2. Only runs for multiple_choice with ≥3 options and a numeric
 *      correctAnswer.
 *   3. Extracts the FIRST sentence embedded between single or double quotes
 *      in the question text. If none → silent (validator does not apply).
 *   4. Tokenizes the sentence into lowercased word tokens (3+ chars, not in
 *      a small Dutch function-word stoplist).
 *   5. For each option, counts how many of its significant tokens appear in
 *      the sentence token set ("anchor coverage").
 *
 * Findings:
 *   - HARD (severity REVIEW, coach): correct option has zero anchor
 *     coverage. This is the classic copy-paste-hallucination signature —
 *     the correct answer talks about something the question sentence does
 *     not even mention.
 *   - SOFT (severity NEAR_LOW, info): correct option has coverage but
 *     ≥ ceil((N-1)/2) distractors have zero coverage. Suggests several
 *     distractors are unrelated to the embedded sentence and probably weak.
 *
 * Out of scope for now:
 *   - Stemming / lemmatization (Dutch morphology). A token-equality match
 *     keeps the heuristic conservative and easy to reason about; false
 *     positives stay rare.
 *   - Non-MC types (fill_in, matching, sentence_parser handle their own
 *     payload-shape semantics in type validators).
 */

import type { ValidationIssue } from '../../issueContract'
import { coach, info } from '../../issueContract'
import type { Question } from '../../../../content'
import { STAGE } from './shared'
import { SEVERITY } from '../../severityScale'

// Conservative Dutch function-word stoplist. Kept small — tokens shorter
// than 3 chars are already filtered, which removes most articles/copulas.
const DUTCH_STOPWORDS = new Set<string>([
  'aan',
  'als',
  'bij',
  'dan',
  'dat',
  'deze',
  'die',
  'door',
  'een',
  'eens',
  'echt',
  'erg',
  'het',
  'hij',
  'ijk',
  'maar',
  'met',
  'naar',
  'nog',
  'ook',
  'onder',
  'over',
  'tot',
  'tot dat',
  'totdat',
  'uit',
  'van',
  'voor',
  'want',
  'was',
  'wat',
  'wel',
  'wie',
  'zijn',
  'zal',
  'zou',
  'zich',
  'haar',
  'hun',
  'mij',
  'ons',
  'jouw',
  'mijn',
  'jou',
  'jij',
  'ben',
  'bent',
  'kan',
  'kun',
  'kunt',
  'omdat',
  'doordat',
  'terwijl',
  'soms',
  'altijd',
  'nooit',
  'heel',
  'erg',
  'even',
  'meer',
  'minder',
  'iets',
  'niets',
  'alles',
])

/**
 * Extract the first quoted sentence in the question text, if any.
 * Recognises straight quotes ('...', "...") and curly Dutch quotes
 * ('...', "..."). Returns null when no quoted span is found.
 */
function extractEmbeddedSentence(questionText: string): string | null {
  if (!questionText) return null

  // Try each quote style; pick the longest span (most likely the
  // embedded sentence, not a single quoted word).
  const patterns: RegExp[] = [
    /"([^"]{4,})"/, // straight double
    /'([^']{4,})'/, // straight single (>=4 chars to skip 'die'/'dat')
    /\u201C([^\u201D]{4,})\u201D/, // curly double
    /\u2018([^\u2019]{4,})\u2019/, // curly single
    /\u201E([^\u201C\u201D]{4,})\u201C/, // Dutch low-9 + left-curly
  ]

  let best: string | null = null
  for (const re of patterns) {
    const m = questionText.match(re)
    if (m && m[1] && (!best || m[1].length > best.length)) {
      best = m[1]
    }
  }
  return best
}

/**
 * Tokenize a string into significant lowercased word tokens.
 * - splits on whitespace and punctuation
 * - drops tokens shorter than 3 chars
 * - drops Dutch function words from the stoplist
 */
function significantTokens(text: string): Set<string> {
  const out = new Set<string>()
  if (!text) return out

  const rawTokens = text
    .toLowerCase()
    .replace(/[.,!?;:()"'\u2018\u2019\u201C\u201D\u201E\-]/g, ' ')
    .split(/\s+/)

  for (const tok of rawTokens) {
    if (!tok) continue
    if (tok.length < 3) continue
    if (DUTCH_STOPWORDS.has(tok)) continue
    out.add(tok)
  }
  return out
}

/**
 * Anchor coverage: number of significant tokens in `option` that also
 * appear in the sentence token set.
 */
function anchorCoverage(option: string, sentenceTokens: Set<string>): number {
  const optTokens = significantTokens(option)
  let hits = 0
  for (const t of optTokens) {
    if (sentenceTokens.has(t)) hits++
  }
  return hits
}

/**
 * P1 CALIBRATION (mei 2026, GOVERNANCE_TAA_TRIAGE):
 *
 * Families whose CORRECT answer is intentionally a category label, an idiom
 * paraphrase, or a synonym/antonym — i.e. NOT a word literally extracted
 * from the embedded sentence. The semantic-overlap heuristic was designed
 * for word-extraction MC ("wat is het onderwerp in deze zin?"); on these
 * families it fires ~22 false positives because the correct option will
 * never share tokens with the stimulus by design.
 *
 * Skipping is keyed on familyId prefix. Adding a new classification family
 * = add its prefix here, no other changes required.
 */
const SKIP_FAMILY_PREFIXES: readonly string[] = [
  'TAA-TEKSTSOORT',
  'TAA-WOORD-', // CONTEXT, SYNONIEM, ANTONIEM, IDIOOM — all paraphrase/category
  'TAA-SOORT-', // word-class labels (lidwoord, bijwoord, ...)
  'TAA-SIGNAALWOORD',
  'TAA-ARGUMENTATIE',
  'TAA-LEZEN-', // reading comprehension — answer is a paraphrase, not a quote
  // P3 CALIBRATION (juni 2026, GOVERNANCE_TRIAGE_SPR_LEEN): SpellingRegel-leenwoord
  // families embed a Dutch context sentence ("kado", "de liefde voor wijsheid")
  // as a meaning-cue. The CORRECT option is the loanword's canonical spelling
  // ("cadeau", "filosofie") which by design does NOT share tokens with the cue
  // sentence (different language, different orthography). Adding the SPR-LEEN-
  // prefix covers GRIEKS / LATIJN / FRANS / ENGELS / C-S-K in one shot.
  'SPR-LEEN-',
  // P4 CALIBRATION (juni 2026, GOVERNANCE_REVIEW_TRIAGE batch 36-104):
  // SpellingWW-leenwoord families (SWW-LEEN-ENG-KOF, SWW-LEEN-ENG-STAM,
  // SWW-LEEN-FRANS) follow the SAME pattern as SPR-LEEN-*. The embedded
  // sentence shows the verb in a Dutch phonetic form ("Hij timed altijd alles
  // precies", "De stam van racen is rac"); the CORRECT option is the
  // conjugated/spelled form per the kofschip / verb-table rule
  // ("timet — Nederlandse hij-vorm krijgt -t", "race — bij Engelse leenwoorden
  // behoud je de Engelse spelling"). The didactic answer is rule-aware
  // conjugation, not a token from the sentence. Triggered by items 38/49/62.
  'SWW-LEEN-',
  // P4 CALIBRATION (juni 2026, GOVERNANCE_REVIEW_TRIAGE batch 36-104):
  // TAA-ONT-* (sentence-parsing / ontleden) families have TWO sub-patterns
  // that both produce guaranteed false positives:
  //   (a) Category-label answers — e.g. TAA-ONT-HOOFDZIN-001 correct =
  //       "2 hoofdzinnen" for sentence "De zon schijnt en de vogels zingen",
  //       or TAA-ONT-BWB-004 correct = "bijwoordelijke bepaling van reden"
  //       for sentence "vanwege de regen". The answer is a grammatical
  //       classification, not a word lifted from the sentence.
  //   (b) Stopword conjunction answers — e.g. TAA-ONT-HOOFDZIN-002 correct =
  //       "want" for sentence "Ik ga naar huis, want ik ben moe". "want" IS
  //       literally in the sentence, but DUTCH_STOPWORDS filters it out as a
  //       function word, so anchor-coverage drops to 0 by tokenizer design.
  // Both sub-patterns are inherent to ontleden-MC and not author errors;
  // skipping the family-prefix is the correct calibration.
  'TAA-ONT-',
]

function isClassificationFamily(familyId: string | undefined): boolean {
  if (!familyId) return false
  return SKIP_FAMILY_PREFIXES.some((p) => familyId.startsWith(p))
}

/**
 * Meta-judgment detection (juni 2026):
 *
 * If the CORRECT option is a meta-judgment like "Alleen zin A is correct",
 * "Beide zinnen zijn correct" or "Geen van beide is correct", the question is
 * asking the learner to judge which of two embedded example sentences uses
 * the correct grammar — the answer is the judgment label itself, not a word
 * lifted from one of the sentences. The semantic-overlap heuristic always
 * fails for these by design.
 *
 * Triggered initially by SWW-TT-MEERVOUD-001 ("Alleen zin B is correct"); the
 * detector is family-agnostic so any module that adopts the same MC pattern
 * is covered without further allowlisting.
 */
function isMetaJudgmentOption(option: string): boolean {
  if (!option) return false
  const normalized = option.trim().toLowerCase()
  return (
    /^alleen zin [ab]\b/.test(normalized) ||
    /^beide zinnen\b/.test(normalized) ||
    /^geen van beide\b/.test(normalized)
  )
}

/**
 * Cloze detection (juni 2026):
 *
 * If the embedded sentence contains a fill-in-the-blank marker — ASCII
 * triple dot "...", Unicode horizontal ellipsis "…", or a 2+-dot gap
 * surrounded by whitespace — the correct option IS the missing word by
 * design and will NEVER share tokens with the sentence. The semantic
 * coherence heuristic is a false positive for these.
 *
 * Triggered initially by SWW-TT-STAM "Ik ... elke dag naar school." /
 * correct = "fiets". Applies family-agnostically: any module that uses
 * cloze sentences (SWW conjugation, SPR spelling, TAA word-context) is
 * covered by the marker check without needing per-family allowlists.
 */
function isClozeSentence(sentence: string): boolean {
  if (!sentence) return false
  // ASCII triple-dot or Unicode ellipsis as a standalone token.
  if (/(^|\s)(\.{2,}|\u2026)(\s|$|[.,!?;:])/.test(sentence)) return true
  return false
}

export function validateSemanticDistractorCoherence(
  question: Question,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  if (question.questionType !== 'multiple_choice') return issues
  const module = question.module
  const isLanguageModule =
    module === 'Taalbegrip' ||
    module === 'SpellingRegel' ||
    module === 'SpellingWW'
  if (!isLanguageModule) return issues

  // P1: skip classification/paraphrase families — see SKIP_FAMILY_PREFIXES.
  if (isClassificationFamily(question.familyId)) return issues

  const opts = question.options ?? []
  const correctIdx = question.correctAnswer
  if (typeof correctIdx !== 'number') return issues
  if (opts.length < 3) return issues
  if (correctIdx < 0 || correctIdx >= opts.length) return issues

  // P1: skip when the correct option is purely numeric (e.g. "3", "twee").
  // Numeric answers never share lexical tokens with a stimulus sentence
  // by design; the overlap heuristic produces false positives.
  const correctOptText = String(opts[correctIdx] ?? '').trim()
  if (/^\d+([.,]\d+)?$/.test(correctOptText)) return issues

  // P3 (juni 2026): skip meta-judgment correct options ("Alleen zin B is correct",
  // "Beide zinnen zijn correct", "Geen van beide is correct"). The answer is the
  // judgment label, not a token from either embedded example sentence — overlap
  // is a guaranteed false positive.
  if (isMetaJudgmentOption(correctOptText)) return issues

  const sentence = extractEmbeddedSentence(question.question ?? '')
  if (!sentence) return issues

  // P2 (juni 2026): skip cloze sentences — by design the answer is the
  // missing word and will not share tokens with the surrounding sentence.
  if (isClozeSentence(sentence)) return issues

  const sentenceTokens = significantTokens(sentence)
  // If the sentence has fewer than 2 significant tokens it's not a usable
  // anchor (e.g. an embedded one-word example). Skip to avoid false positives.
  if (sentenceTokens.size < 2) return issues

  const qId = question.id ?? 'UNKNOWN'
  const fId = question.familyId ?? 'UNKNOWN'

  const correctOpt = opts[correctIdx] ?? ''
  const correctCoverage = anchorCoverage(correctOpt, sentenceTokens)

  // ── HARD: correct answer has zero anchor coverage ─────────────────────
  // The correct option shares no significant words with the embedded
  // sentence. This is the copy-paste-hallucination signature.
  if (correctCoverage === 0) {
    issues.push(
      coach(
        'content',
        STAGE,
        'CONTENT_DISTRACTOR_QUALITY',
        `Correct answer "${correctOpt}" shares no significant words with the embedded sentence "${sentence}" — likely copy-paste hallucination or misaligned options.`,
        {
          field: 'options',
          expected:
            'Correct answer should reference at least one content word from the embedded sentence',
          actual: `correct="${correctOpt}"; sentence tokens=[${[...sentenceTokens].slice(0, 8).join(', ')}${sentenceTokens.size > 8 ? ', ...' : ''}]`,
          questionId: qId,
          familyId: fId,
          severity: SEVERITY.REVIEW,
          suggestion:
            'Verify that the option strings belong to THIS question. If the question asks about a part of the embedded sentence (e.g. "wat is het lijdend voorwerp in deze zin?"), the correct option should be a word/phrase that actually occurs in that sentence.',
          metadata: {
            reason: 'weak_semantic_coherence',
            subReason: 'correct_zero_anchor',
            sentence,
            correctOption: correctOpt,
          },
        },
      ),
    )
    return issues
  }

  // ── SOFT: ≥ ceil((N-1)/2) distractors have zero anchor coverage ────────
  const distractorCoverages: Array<{ idx: number; cov: number; text: string }> =
    []
  for (let i = 0; i < opts.length; i++) {
    if (i === correctIdx) continue
    distractorCoverages.push({
      idx: i,
      cov: anchorCoverage(opts[i] ?? '', sentenceTokens),
      text: opts[i] ?? '',
    })
  }
  const zeroCovDistractors = distractorCoverages.filter((d) => d.cov === 0)
  const threshold = Math.ceil(distractorCoverages.length / 2)
  if (
    zeroCovDistractors.length >= threshold &&
    zeroCovDistractors.length >= 2
  ) {
    issues.push(
      info(
        'content',
        STAGE,
        'CONTENT_DISTRACTOR_QUALITY',
        `${zeroCovDistractors.length}/${distractorCoverages.length} distractors share no significant words with the embedded sentence "${sentence}" — distractors may be weak or copy-pasted from elsewhere.`,
        {
          field: 'options',
          questionId: qId,
          familyId: fId,
          severity: SEVERITY.NEAR_LOW,
          suggestion:
            'Strong distractors for sentence-analysis MC questions should also be drawn from the embedded sentence (different roles, same words). Distractors with zero overlap are often generic and easy to eliminate by exclusion.',
          metadata: {
            reason: 'weak_semantic_coherence',
            subReason: 'weak_distractor_anchors',
            sentence,
            zeroCovOptions: zeroCovDistractors.map((d) => ({
              optionIndex: d.idx,
              text: d.text,
            })),
          },
        },
      ),
    )
  }

  return issues
}
